/**
 * /erp/inventory/adjustments — manager-only audit trail + new adjustment form.
 *
 * Two halves:
 *
 *   * Top: a "create adjustment" form that mirrors the modal on the
 *     stock-levels page, but free-form (you pick the item & warehouse
 *     instead of starting from a row). Useful for stock-in events
 *     where the row doesn't exist yet, or for shrinkage audits where
 *     the manager wants to scan the catalog directly.
 *   * Bottom: paged list of past adjustments, newest first, with
 *     filters (warehouse, item) so 'find that adjustment from yesterday'
 *     stays a one-click affair.
 *
 * The list shows the actor, delta, reason, and the qty straddle is
 * computed on the API side at write time (we don't replay history on
 * read because adjustment volumes are low and the qty column on the
 * inventory row can drift due to manufacturing / dispatch flows in
 * later PRs).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, ChevronDown, ChevronRight, ClipboardList, FileUp, Loader2, Plus, Undo2, X } from "lucide-react";
import { Fragment, type FormEvent, useState } from "react";

import {
  ApiError,
  type ErpInventoryAdjustmentEvent,
  type ErpInventoryAdjustmentCreate,
  type ErpInventoryDocumentParsed,
  erpApi,
  isErpCatalogConflict,
  isInventoryNegativeBlocked,
  parseInventoryDocumentAmendment,
} from "../lib/api";
import { useInventoryAdjustments, useProducts, useRawMaterials, useWarehouses } from "../lib/erp";
import { toast } from "../lib/toast";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/erp/inventory/adjustments")({
  component: InventoryAdjustmentsPage,
});

function InventoryAdjustmentsPage() {
  const queryClient = useQueryClient();
  const role = useAuthStore((state) => state.user?.role);
  const canReverse = role === "manager" || role === "admin";
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterRawMaterial, setFilterRawMaterial] = useState("");
  const [page, setPage] = useState(0);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [amendingEvent, setAmendingEvent] = useState<ErpInventoryAdjustmentEvent | null>(null);
  const PAGE_SIZE = 25;

  const warehousesQ = useWarehouses();
  const productsQ = useProducts();
  const rawsQ = useRawMaterials();

  const adjustmentsQ = useInventoryAdjustments({
    ...(filterWarehouse ? { warehouse_id: filterWarehouse } : {}),
    ...(filterProduct ? { product_id: filterProduct } : {}),
    ...(filterRawMaterial ? { raw_material_id: filterRawMaterial } : {}),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const total = adjustmentsQ.data?.total ?? 0;
  const items = adjustmentsQ.data?.items ?? [];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const reverseMutation = useMutation({
    mutationFn: async ({ id, documentId, reason }: { id: string; documentId: string | null; reason: string }) => {
      if (documentId) await erpApi.inventory.reverseDocument(documentId, { reason });
      else await erpApi.inventory.reverseAdjustment(id, { reason });
    },
    onSuccess: () => {
      toast.success("Adjustment reversed. The original trace was retained.");
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Could not reverse adjustment."),
  });

  const reverse = (id: string, documentId: string | null) => {
    const reason = window.prompt("Reason for reversal (required):")?.trim();
    if (reason) reverseMutation.mutate({ id, documentId, reason });
  };

  const toggleExpanded = (eventId: string) => {
    setExpandedEvents((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <NewAdjustmentForm
        warehouses={warehousesQ.data?.items ?? []}
        products={productsQ.data?.items ?? []}
        rawMaterials={rawsQ.data?.items ?? []}
      />

      <section className="space-y-3">
        <header className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight">
            <ClipboardList className="size-4 opacity-70" aria-hidden />
            Adjustment history
          </h2>
          <span className="text-xs opacity-60">{total} records</span>
        </header>

        <div className="flex flex-wrap gap-3 rounded-xl border border-[var(--border)] p-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium opacity-80">Warehouse</span>
            <select
              value={filterWarehouse}
              onChange={(e) => {
                setFilterWarehouse(e.target.value);
                setPage(0);
              }}
              className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">All warehouses</option>
              {(warehousesQ.data?.items ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} — {w.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium opacity-80">Product</span>
            <select
              value={filterProduct}
              onChange={(e) => {
                setFilterProduct(e.target.value);
                setFilterRawMaterial("");
                setPage(0);
              }}
              className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">Any product</option>
              {(productsQ.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium opacity-80">Raw material</span>
            <select
              value={filterRawMaterial}
              onChange={(e) => {
                setFilterRawMaterial(e.target.value);
                setFilterProduct("");
                setPage(0);
              }}
              className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">Any raw material</option>
              {(rawsQ.data?.items ?? []).map((rm) => (
                <option key={rm.id} value={rm.id}>
                  {rm.code} — {rm.name}
                </option>
              ))}
            </select>
          </label>
          {(filterWarehouse || filterProduct || filterRawMaterial) && (
            <button
              type="button"
              onClick={() => {
                setFilterWarehouse("");
                setFilterProduct("");
                setFilterRawMaterial("");
                setPage(0);
              }}
              className="self-end rounded-md border border-[var(--border)] px-2 py-1.5 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              Clear filters
            </button>
          )}
        </div>

        {adjustmentsQ.isLoading ? (
          <p className="flex items-center gap-2 text-sm opacity-60">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Loading…
          </p>
        ) : adjustmentsQ.error ? (
          <p className="text-sm text-red-700 dark:text-red-300">
            Could not load adjustments: {(adjustmentsQ.error as Error).message}
          </p>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
            <ClipboardList className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
            <p className="text-sm font-medium">No adjustments match.</p>
            <p className="mt-1 text-sm opacity-60">
              Try clearing the filters or applying a stock correction above.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--border)] bg-black/[0.02] text-left text-xs uppercase tracking-wider opacity-70 dark:bg-white/[0.02]">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Record</th>
                  <th className="px-3 py-2">Warehouse</th>
                  <th className="px-3 py-2">Items updated</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">By</th>
                  {canReverse && <th className="px-3 py-2 text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((event) => {
                  const isExpanded = expandedEvents.has(event.id);
                  const firstLine = event.lines[0];
                  const documentLabel =
                    event.movement_type === "transfer"
                      ? "Warehouse transfer"
                      : event.document_type === "delivery_challan"
                        ? "Delivery challan"
                        : event.document_type === "sales_return"
                          ? "Sales return / Credit note"
                          : "Invoice";
                  const itemCount = event.event_type === "document"
                    ? new Set(event.lines.map((line) => line.product_id ?? line.raw_material_id ?? line.id)).size
                    : event.lines.length;
                  return (
                    <Fragment key={event.id}>
                      <tr className="border-b border-[var(--border)]">
                        <td className="whitespace-nowrap px-3 py-3 text-xs opacity-70">
                          {new Date(event.created_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-3">
                          {event.event_type === "document" ? (
                            <div className="space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                                  {documentLabel}
                                </span>
                                {event.status !== "active" && (
                                  <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] uppercase tracking-wide opacity-60 dark:bg-white/10">
                                    {event.status}
                                  </span>
                                )}
                              </div>
                              <span className="font-mono text-xs font-medium">{event.document_number}</span>
                              {event.document_date && <p className="text-xs opacity-60">Dated {event.document_date}</p>}
                            </div>
                        ) : (
                            <div className="space-y-1">
                              <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide dark:bg-white/10">
                                Manual adjustment
                              </span>
                              {event.status !== "active" && <p className="text-xs opacity-60">{event.status}</p>}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {event.warehouse_code ? (
                            <>
                              <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                                {event.warehouse_code}
                              </span>{" "}
                              <span className="opacity-80">{event.warehouse_name}</span>
                              {event.movement_type === "transfer" && event.destination_warehouse_code && (
                                <>
                                  <span className="mx-1 opacity-50">→</span>
                                  <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                                    {event.destination_warehouse_code}
                                  </span>{" "}
                                  <span className="opacity-80">{event.destination_warehouse_name}</span>
                                </>
                              )}
                            </>
                        ) : (
                            <span className="opacity-50">(deleted)</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {event.event_type === "document" ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(event.id)}
                              aria-expanded={isExpanded}
                              className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[var(--border)] px-2.5 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5"
                            >
                              {isExpanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                              {itemCount} item{itemCount === 1 ? "" : "s"}
                            </button>
                          ) : (
                            <div>
                              <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                                {firstLine?.item_code ?? "Deleted item"}
                              </span>{" "}
                              <span>{firstLine?.item_name}</span>
                            </div>
                          )}
                        </td>
                        <td className="max-w-xs px-3 py-3 text-xs">{event.reason}</td>
                        <td className="px-3 py-3 text-xs opacity-70">{event.created_by_name ?? "–"}</td>
                        {canReverse && (
                          <td className="px-3 py-3 text-right">
                            <div className="flex flex-wrap justify-end gap-2">
                            {role === "admin" && event.event_type === "document" && event.status === "active" && (
                              <button
                                type="button"
                                onClick={() => setAmendingEvent(event)}
                                className="inline-flex min-h-9 items-center gap-1 rounded-md border border-[var(--border)] px-2 text-xs"
                              >
                                <FileUp className="size-3.5" />
                                Amend from PDF
                              </button>
                            )}
                            {event.status === "reversal" ? (
                              <span className="text-xs opacity-50">Reversal</span>
                            ) : event.status === "reversed" ? (
                              <span className="text-xs opacity-50">Reversed</span>
                            ) : firstLine ? (
                              <button
                                type="button"
                                onClick={() => reverse(firstLine.id, event.document_id)}
                                disabled={reverseMutation.isPending}
                                className="inline-flex min-h-9 items-center gap-1 rounded-md border border-[var(--border)] px-2 text-xs"
                              >
                                <Undo2 className="size-3.5" />
                                {event.document_id ? "Reverse document" : "Reverse"}
                              </button>
                            ) : null}
                            </div>
                          </td>
                        )}
                      </tr>
                      {event.event_type === "document" && isExpanded && (
                        <tr className="border-b border-[var(--border)] bg-black/[0.015] dark:bg-white/[0.02]">
                          <td colSpan={canReverse ? 7 : 6} className="px-4 py-3">
                            <div className="overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--bg)]">
                              <table className="w-full min-w-[650px] text-xs">
                                <thead className="border-b border-[var(--border)] text-left uppercase tracking-wide opacity-60">
                                  <tr>
                                    <th className="px-3 py-2">Item</th>
                                    <th className="px-3 py-2">Warehouse</th>
                                    <th className="px-3 py-2 text-right">Change</th>
                                    <th className="px-3 py-2 text-right">Resulting stock</th>
                                    <th className="px-3 py-2">Line status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {event.lines.map((line) => {
                                    const delta = Number(line.delta);
                                    return (
                                      <tr key={line.id} className="border-b border-[var(--border)] last:border-0">
                                        <td className="px-3 py-2.5">
                                          <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                                            {line.item_code ?? "Deleted item"}
                                          </span>{" "}
                                          <span>{line.item_name}</span>
                                        </td>
                                        <td className="px-3 py-2.5">
                                          <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                                            {line.warehouse_code ?? "Deleted"}
                                          </span>{" "}
                                          <span className="opacity-70">{line.warehouse_name}</span>
                                        </td>
                                        <td className={`px-3 py-2.5 text-right font-mono ${delta >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}`}>
                                          {delta > 0 ? "+" : ""}{line.delta}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono">{line.qty_after ?? "–"}</td>
                                        <td className="px-3 py-2.5 opacity-60">
                                          {line.reversal_of_id ? "Reversal" : line.reversed_by_adjustment_id ? "Reversed" : line.reason.startsWith("Document amendment:") ? "Amendment" : "Applied"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 text-xs">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-[var(--border)] px-2 py-1 hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              Previous
            </button>
            <span className="opacity-70">
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages}
              className="rounded-md border border-[var(--border)] px-2 py-1 hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              Next
            </button>
          </div>
        )}
      </section>
      {amendingEvent?.document_id && (
        <DocumentAmendmentModal
          event={amendingEvent}
          onClose={() => setAmendingEvent(null)}
        />
      )}
    </div>
  );
}

function DocumentAmendmentModal({
  event,
  onClose,
}: {
  event: ErpInventoryAdjustmentEvent;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [parsed, setParsed] = useState<ErpInventoryDocumentParsed | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const documentId = event.document_id as string;

  const selectFile = (nextFile: File | null) => {
    if (!nextFile) return;

    const isPdf = nextFile.type === "application/pdf" || nextFile.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setFile(null);
      setParsed(null);
      setError("Choose a PDF challan or invoice.");
      return;
    }
    if (nextFile.size > 10 * 1024 * 1024) {
      setFile(null);
      setParsed(null);
      setError("The PDF is larger than the 10 MB limit.");
      return;
    }

    setFile(nextFile);
    setParsed(null);
    setError(null);
  };

  const parseMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Choose the original PDF first.");
      return parseInventoryDocumentAmendment(documentId, file);
    },
    onSuccess: (result) => {
      setParsed(result);
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Could not compare this PDF."),
  });

  const applyMutation = useMutation({
    mutationFn: () => {
      if (!parsed) throw new Error("Compare the PDF first.");
      if (reason.trim().length < 10) throw new Error("Enter a correction reason of at least 10 characters.");
      return erpApi.inventory.applyDocumentAmendment(documentId, {
        parse_token: parsed.parse_token,
        reason: reason.trim(),
        lines: parsed.lines.map((line) => ({
          source_sku: line.source_sku,
          product_id: line.product_id as string,
          description: line.description,
          quantity: line.quantity,
          uom: line.uom,
        })),
      });
    },
    onSuccess: (result) => {
      toast.success(`${result.document_number} amended with ${result.lines.length} missing item${result.lines.length === 1 ? "" : "s"}.`);
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Could not apply the amendment."),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="amend-document-title">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/50" onClick={onClose} />
      <section className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-xl">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 id="amend-document-title" className="text-lg font-semibold">Amend document from PDF</h2>
            <p className="mt-1 text-sm opacity-65">{event.document_number} · Upload the exact original PDF. Only missing quantities will be proposed.</p>
            {event.movement_type === "transfer" && (
              <p className="mt-1 text-xs opacity-60">{event.warehouse_code} → {event.destination_warehouse_code}</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-2 hover:bg-black/5 dark:hover:bg-white/5"><X className="size-5" /></button>
        </header>

        {!parsed ? (
          <div className="mt-5 space-y-4">
            <label
              className={`block cursor-pointer rounded-xl border-2 border-dashed p-7 text-center transition-colors ${
                isDraggingFile
                  ? "border-[var(--accent)] bg-blue-500/10"
                  : "border-[var(--border)] hover:border-[var(--accent)]"
              }`}
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "copy";
                setIsDraggingFile(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setIsDraggingFile(false);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDraggingFile(false);
                selectFile(event.dataTransfer.files?.[0] ?? null);
              }}
            >
              <FileUp className="mx-auto mb-3 size-8 opacity-60" />
              <span className="block font-medium">
                {isDraggingFile ? "Drop the original PDF here" : file?.name ?? "Drag and drop the original PDF here"}
              </span>
              <span className="mt-1 block text-xs opacity-60">or click to browse · PDF only · maximum 10 MB</span>
              <span className="mt-1 block text-xs opacity-60">The file fingerprint must match the original upload.</span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                onChange={(e) => {
                  selectFile(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
            </label>
            <div className="flex justify-end">
              <button type="button" disabled={!file || parseMutation.isPending} onClick={() => parseMutation.mutate()} className="inline-flex min-h-10 items-center gap-2 rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white disabled:opacity-50">
                {parseMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                Compare with applied record
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
              The following quantities exist in the PDF but not in the applied record. Review them before amending stock.
            </div>
            <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
              <table className="w-full min-w-[650px] text-sm">
                <thead className="border-b border-[var(--border)] text-left text-xs uppercase tracking-wide opacity-60">
                  <tr>
                    <th className="px-3 py-2">PDF SKU</th><th className="px-3 py-2">Matched product</th><th className="px-3 py-2 text-right">Missing qty</th>
                    <th className="px-3 py-2 text-right">{parsed.direction === "transfer" ? "From current" : "Current"}</th>
                    <th className="px-3 py-2 text-right">{parsed.direction === "transfer" ? "From after" : "After"}</th>
                    {parsed.direction === "transfer" && <><th className="px-3 py-2 text-right">To current</th><th className="px-3 py-2 text-right">To after</th></>}
                  </tr>
                </thead>
                <tbody>
                  {parsed.lines.map((line) => (
                    <tr key={line.product_id} className="border-b border-[var(--border)] last:border-0">
                      <td className="px-3 py-3 font-mono text-xs">{line.source_sku}</td>
                      <td className="px-3 py-3"><span className="font-mono text-xs">{line.product_code}</span> · {line.product_name}</td>
                      <td className="px-3 py-3 text-right font-mono">{line.quantity} {line.uom}</td>
                      <td className="px-3 py-3 text-right font-mono">{line.current_qty ?? "–"}</td>
                      <td className="px-3 py-3 text-right font-mono">{line.resulting_qty ?? "–"}</td>
                      {parsed.direction === "transfer" && (
                        <>
                          <td className="px-3 py-3 text-right font-mono">{line.destination_current_qty ?? "–"}</td>
                          <td className="px-3 py-3 text-right font-mono">{line.destination_resulting_qty ?? "–"}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="block space-y-1">
              <span className="text-xs font-medium">Correction reason <span className="text-red-500">*</span></span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Example: Items 16–21 were omitted by the original PDF extraction parser." className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm" />
            </label>
            <div className="flex flex-wrap justify-between gap-3">
              <button type="button" onClick={() => setParsed(null)} className="rounded-md border border-[var(--border)] px-3 py-2 text-sm">Choose another PDF</button>
              <button type="button" disabled={applyMutation.isPending || reason.trim().length < 10} onClick={() => applyMutation.mutate()} className="inline-flex min-h-10 items-center gap-2 rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white disabled:opacity-50">
                {applyMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                Apply audited amendment
              </button>
            </div>
          </div>
        )}
        {error && <p className="mt-4 text-sm text-red-700 dark:text-red-300">{error}</p>}
      </section>
    </div>
  );
}

function NewAdjustmentForm({
  warehouses,
  products,
  rawMaterials,
}: {
  warehouses: { id: string; code: string; name: string; is_active: boolean }[];
  products: { id: string; code: string; name: string; is_active: boolean }[];
  rawMaterials: { id: string; code: string; name: string; is_active: boolean }[];
}) {
  const queryClient = useQueryClient();
  const [itemKind, setItemKind] = useState<"product" | "raw_material">("product");
  const [productId, setProductId] = useState("");
  const [rawMaterialId, setRawMaterialId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [allowNegative, setAllowNegative] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: ErpInventoryAdjustmentCreate) => erpApi.inventory.createAdjustment(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
      const wMsg = data.warned ? " (qty went negative)" : "";
      toast.success(`Stock now ${data.qty_after}${wMsg}`);
      setDelta("");
      setReason("");
      setAllowNegative(false);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        if (isInventoryNegativeBlocked(err.detail)) {
          setError(
            `Already at qty ${err.detail.current_qty}. Tick 'Allow negative' to push further into the red.`,
          );
          return;
        }
        if (isErpCatalogConflict(err.detail)) {
          setError(err.detail.message);
          return;
        }
      }
      const msg = err instanceof ApiError ? err.message : "Could not save adjustment.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!warehouseId) return setError("Pick a warehouse.");
    if (itemKind === "product" && !productId) return setError("Pick a product.");
    if (itemKind === "raw_material" && !rawMaterialId) return setError("Pick a raw material.");
    const trimmed = delta.trim();
    if (!trimmed || trimmed === "0") return setError("Enter a non-zero delta.");
    if (!reason.trim()) return setError("Reason is required.");

    const body: ErpInventoryAdjustmentCreate = {
      item_kind: itemKind,
      warehouse_id: warehouseId,
      delta: trimmed,
      reason: reason.trim(),
      allow_negative: allowNegative,
    };
    if (itemKind === "product") body.product_id = productId;
    else body.raw_material_id = rawMaterialId;
    mutation.mutate(body);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-[var(--border)] p-4">
      <header className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight">
          <Plus className="size-4 opacity-70" aria-hidden />
          New adjustment
        </h2>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Item kind</span>
          <select
            value={itemKind}
            onChange={(e) => {
              setItemKind(e.target.value as "product" | "raw_material");
              setProductId("");
              setRawMaterialId("");
            }}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          >
            <option value="product">Product</option>
            <option value="raw_material">Raw material</option>
          </select>
        </label>

        {itemKind === "product" ? (
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">
              Product <span className="text-red-500">*</span>
            </span>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              required
            >
              <option value="">— Pick product —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                  {!p.is_active ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">
              Raw material <span className="text-red-500">*</span>
            </span>
            <select
              value={rawMaterialId}
              onChange={(e) => setRawMaterialId(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              required
            >
              <option value="">— Pick raw material —</option>
              {rawMaterials.map((rm) => (
                <option key={rm.id} value={rm.id}>
                  {rm.code} — {rm.name}
                  {!rm.is_active ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Warehouse <span className="text-red-500">*</span>
          </span>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            required
          >
            <option value="">— Pick warehouse —</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
                {!w.is_active ? " (inactive)" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Delta <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="e.g. +10 or -2.5"
            inputMode="decimal"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium opacity-80">
          Reason <span className="text-red-500">*</span>
        </span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder="Why is this needed? (audit log entry)"
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
        />
      </label>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={allowNegative}
          onChange={(e) => setAllowNegative(e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border border-[var(--border)]"
        />
        <span>Allow further-negative (manager override)</span>
      </label>

      {error && (
        <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
          <AlertCircle className="size-3" aria-hidden />
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Plus className="size-4" aria-hidden />
          )}
          Save adjustment
        </button>
      </div>
    </form>
  );
}
